import React, { useState, useEffect } from 'react';
import { X, Phone, Mail, Calendar, TrendingUp, Clock, DollarSign, Home, AlertCircle, CheckCircle, Target, MessageSquare } from 'lucide-react';

const CallAnalysisModal = ({ leadId, isOpen, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && leadId) {
      fetchAnalysis();
    }
  }, [isOpen, leadId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/leads/${leadId}/analysis`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analysis');
      }
      
      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTemperatureColor = (temp) => {
    switch (temp?.toLowerCase()) {
      case 'hot': return 'text-red-500 bg-red-50';
      case 'warm': return 'text-orange-500 bg-orange-50';
      case 'cold': return 'text-blue-500 bg-blue-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'immediate': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatTranscript = (transcript) => {
    if (!transcript) return [];
    
    // Split by speaker lines
    const lines = transcript.split('\n');
    return lines.map((line, index) => {
      const [speaker, ...messageParts] = line.split(':');
      const message = messageParts.join(':').trim();
      return { speaker: speaker.trim(), message, id: index };
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
                {analysis?.lead_name || 'Lead Analysis'}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Complete call details and AI-powered insights
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <AlertCircle className="w-16 h-16 mb-4" />
              <p className="text-lg font-semibold">{error}</p>
              <button
                onClick={fetchAnalysis}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : !analysis?.has_analysis ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <MessageSquare className="w-16 h-16 mb-4" />
              <p className="text-lg font-semibold">No call analysis available</p>
              <p className="text-sm mt-2">This lead hasn't been called yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-slate-700 dark:to-slate-600 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">Lead Score</p>
                      <p className={`text-3xl font-bold ${getScoreColor(analysis.analysis?.qualification_score)}`}>
                        {analysis.analysis?.qualification_score || 0}%
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-slate-700 dark:to-slate-600 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">Temperature</p>
                      <p className={`text-xl font-bold ${getTemperatureColor(analysis.analysis?.lead_temperature)}`}>
                        {analysis.analysis?.lead_temperature?.toUpperCase() || 'N/A'}
                      </p>
                    </div>
                    <Target className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-slate-700 dark:to-slate-600 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">Call Duration</p>
                      <p className="text-xl font-bold text-purple-600">
                        {formatDuration(analysis.last_call_duration)}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-purple-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-slate-700 dark:to-slate-600 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">Priority</p>
                      <p className="text-xl font-bold text-orange-600">
                        {analysis.analysis?.recommended_priority?.toUpperCase() || 'N/A'}
                      </p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-orange-500" />
                  </div>
                </div>
              </div>

              {/* AI-Powered Summary */}
              {analysis.analysis?.ai_summary && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-slate-700 dark:to-slate-600 p-6 rounded-xl border-l-4 border-green-500">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                        AI-Powered Analysis Summary
                      </h3>
                      <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                        {analysis.analysis.ai_summary}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Key Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Intent */}
                {analysis.analysis?.intent && (
                  <div className="bg-white dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <Target className="w-5 h-5 text-blue-500" />
                      <h4 className="font-semibold text-slate-800 dark:text-white">Intent</h4>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 capitalize">
                      {analysis.analysis.intent}
                    </p>
                  </div>
                )}

                {/* Budget */}
                {analysis.analysis?.budget && (
                  <div className="bg-white dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <h4 className="font-semibold text-slate-800 dark:text-white">Budget</h4>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300">{analysis.analysis.budget}</p>
                  </div>
                )}

                {/* Timeline */}
                {analysis.analysis?.timeline && (
                  <div className="bg-white dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="w-5 h-5 text-purple-500" />
                      <h4 className="font-semibold text-slate-800 dark:text-white">Timeline</h4>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300">{analysis.analysis.timeline}</p>
                  </div>
                )}

                {/* Decision Maker */}
                {analysis.analysis?.decision_maker && (
                  <div className="bg-white dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-indigo-500" />
                      <h4 className="font-semibold text-slate-800 dark:text-white">Decision Maker</h4>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 capitalize">
                      {analysis.analysis.decision_maker}
                    </p>
                  </div>
                )}
              </div>

              {/* Property Preferences */}
              {analysis.analysis?.property_preferences && (
                <div className="bg-white dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-3">
                    <Home className="w-5 h-5 text-blue-500" />
                    <h4 className="font-semibold text-slate-800 dark:text-white">Property Preferences</h4>
                  </div>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                    {analysis.analysis.property_preferences}
                  </p>
                </div>
              )}

              {/* Pain Points & Motivations */}
              {(analysis.analysis?.pain_points || analysis.analysis?.motivation) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.analysis?.pain_points && (
                    <div className="bg-red-50 dark:bg-slate-700 p-4 rounded-lg border border-red-200 dark:border-slate-600">
                      <h4 className="font-semibold text-red-800 dark:text-red-400 mb-2">Pain Points</h4>
                      <p className="text-slate-700 dark:text-slate-300">{analysis.analysis.pain_points}</p>
                    </div>
                  )}
                  
                  {analysis.analysis?.motivation && (
                    <div className="bg-green-50 dark:bg-slate-700 p-4 rounded-lg border border-green-200 dark:border-slate-600">
                      <h4 className="font-semibold text-green-800 dark:text-green-400 mb-2">Motivation</h4>
                      <p className="text-slate-700 dark:text-slate-300">{analysis.analysis.motivation}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Next Steps */}
              {analysis.analysis?.next_steps && analysis.analysis.next_steps.length > 0 && (
                <div className="bg-blue-50 dark:bg-slate-700 p-6 rounded-lg border border-blue-200 dark:border-slate-600">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-400 mb-4 text-lg">
                    Recommended Next Steps
                  </h4>
                  <ul className="space-y-2">
                    {analysis.analysis.next_steps.map((step, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </span>
                        <span className="text-slate-700 dark:text-slate-300 flex-1">{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Insights */}
              {analysis.analysis?.key_insights && analysis.analysis.key_insights.length > 0 && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-slate-700 dark:to-slate-600 p-6 rounded-lg">
                  <h4 className="font-semibold text-purple-800 dark:text-purple-400 mb-4 text-lg">
                    Key Insights
                  </h4>
                  <div className="space-y-3">
                    {analysis.analysis.key_insights.map((insight, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-slate-700 dark:text-slate-300">{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Conversation Transcript */}
              {analysis.last_call_transcript && (
                <div className="bg-white dark:bg-slate-700 p-6 rounded-lg border border-slate-200 dark:border-slate-600">
                  <h4 className="font-semibold text-slate-800 dark:text-white mb-4 text-lg flex items-center space-x-2">
                    <MessageSquare className="w-5 h-5" />
                    <span>Full Conversation Transcript</span>
                  </h4>
                  <div className="space-y-4 max-h-96 overflow-y-auto bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                    {formatTranscript(analysis.last_call_transcript).map((line) => (
                      <div key={line.id} className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                          {line.speaker}
                        </p>
                        <p className="text-slate-700 dark:text-slate-300 pl-4 border-l-2 border-blue-500">
                          {line.message}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {analysis?.has_analysis && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex justify-between items-center">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Call Date: {analysis.last_call_at ? new Date(analysis.last_call_at).toLocaleString() : 'N/A'}
            </div>
            <div className="flex space-x-3">
              {analysis.last_call_recording_url && (
                <a
                  href={analysis.last_call_recording_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2"
                >
                  <Phone className="w-4 h-4" />
                  <span>Listen to Recording</span>
                </a>
              )}
              <button 
                onClick={async () => {
                  try {
                    // Get lead phone from analysis
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${backendUrl}/api/leads/${leadId}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const lead = await response.json();
                    window.location.href = `tel:${lead.phone}`;
                  } catch (err) {
                    console.error('Error calling lead:', err);
                  }
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
              >
                <Phone className="w-4 h-4" />
                <span>Call Lead</span>
              </button>
              <button 
                onClick={async () => {
                  try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${backendUrl}/api/leads/${leadId}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const lead = await response.json();
                    const subject = `Follow up regarding ${analysis.analysis?.property_preferences || 'your property search'}`;
                    const body = `Hi ${analysis.lead_name},\n\nI wanted to follow up on our recent conversation about your ${analysis.analysis?.intent || 'property'} search.\n\nBased on our discussion, I have some properties that match your criteria:\n- Budget: ${analysis.analysis?.budget || 'As discussed'}\n- Timeline: ${analysis.analysis?.timeline || 'As discussed'}\n\nWhen would be a good time to discuss these options?\n\nBest regards`;
                    window.location.href = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  } catch (err) {
                    console.error('Error sending email:', err);
                  }
                }}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>Send Email</span>
              </button>
              <button 
                onClick={() => {
                  alert('Schedule Follow-up feature coming soon! This will integrate with your calendar to schedule appointments.');
                }}
                className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center space-x-2"
              >
                <Calendar className="w-4 h-4" />
                <span>Schedule Follow-up</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallAnalysisModal;
